import { Subject } from 'rxjs';
import { ButtonBinder } from '../../binders/visual';
import { FlowyFoundation } from '../../flowy';
import {
  IGizmoEvent,
  IRootGizmoCallbacks,
  IRootGizmoData,
  IError,
  IPlatformCallbacks,
  IGizmo,
  TJsonData,
} from '../../types';
import Gizmo from '../gizmo';
import RootGizmo from '../root-gizmo';
import SectionStateMachine from '../section-state-machine';
import { findGizmos, runHandlerOnGizmosTree } from '../utils';
import { flowyToJson } from './json';

interface IParams {
  event: IGizmoEvent;
  context: {
    rootGizmo: RootGizmo;
    parentRootGizmoFid?: string;
    callbacks: IRootGizmoCallbacks;
    platformCallbacks?: IPlatformCallbacks;
    events: Subject<IGizmoEvent>;
    data?: IRootGizmoData;
    jsonData?: TJsonData;
    foundation?: FlowyFoundation;
    sectionStateMachine?: SectionStateMachine;
  };
}

/**
 * RootGizmo function to handle a GizmoEvent
 *
 * @param {IGizmoEvent} event gizmo event
 * @returns
 */
function handleEvent({ event, context }: IParams): Promise<void> {
  return new Promise<void>(async resolve => {
    const {
      rootGizmo,
      callbacks,
      platformCallbacks,
      data,
      jsonData,
      events,
      parentRootGizmoFid,
      foundation,
      sectionStateMachine,
    } = context;

    if (event.type === 'gizmo.operation') {
      if (event.data?.gizmoOperation?.operation === 'form.submit') {
        const hiddenSectionFids = sectionStateMachine
          ?.getStates()
          ?.filter(s => !s.display)
          .map(s => s.sectionFid);
        const sections = rootGizmo.findGizmos({ type: 'section' });
        // console.log('ssssssssssssssssssssssssssssssss: ', sections);
        let validationErrors: IError[] = await runHandlerOnGizmosTree({
          gizmo: rootGizmo,
          handler: 'validateHDLR',
          // withoutServer: true,
          withoutServer: sections.length > 0,
        });
        validationErrors = validationErrors
          .filter(vE => vE.features?.display)
          .filter(vE => {
            // Just in case there are no sections
            if (!hiddenSectionFids) return true;

            // If there are sections we're going to check we don't have any of the
            // hidden sections as ancestor
            const ancestorsFids = vE.ancestors?.map(a => a?.fid);
            const intersection = ancestorsFids?.filter(aFid =>
              hiddenSectionFids?.includes(aFid)
            );

            return intersection?.length === 0;
          });

        const rootGizmoValidationErrors = await rootGizmo.validate();

        if (
          validationErrors.length === 0 &&
          rootGizmoValidationErrors.length === 0 &&
          data
        ) {
          data.epoch_end = Math.floor(Date.now() / 1000);
          data.source = 'web';

          let jData: TJsonData | undefined;
          if (jsonData) {
            jData = flowyToJson({
              config: rootGizmo.getConfig(),
              data,
            });
          }

          await callbacks.submitRootGizmoDataCB({
            rootGizmoData: data,
            jsonData,
            // This function will be called by the Foundation layer to notify the RootGizmo
            // the submit has been finished. Right now this basically is used in order to create
            // an event which will be listened by the 'submit' button in order to reset the
            // 'processing' feature
            onSubmitFinish: () => {
              const afterSubmitEvent: IGizmoEvent = {
                type: 'root-gizmo.operation',
                src: {
                  type: 'root-gizmo',
                  rootGizmoFid: rootGizmo.getConfig().fid,
                },
                dest: {
                  type: 'gizmo',
                  gizmoFid: event.src.gizmoFid,
                  gizmoUid: event.src.gizmoUid,
                },
                data: {
                  rootGizmoOperation: {
                    type: 'form.lifecycle.after-submit',
                  },
                },
              };

              events.next(afterSubmitEvent);
            },
          });
        } else if (
          validationErrors.length > 0 ||
          rootGizmoValidationErrors.length > 0
        ) {
          // If there are validation errors, we're going to send an event so the gizmos that
          // have the processing flag on true will unset it
          const event: IGizmoEvent = {
            type: 'root-gizmo.operation',
            src: {
              type: 'root-gizmo',
              rootGizmoFid: rootGizmo.getConfig().fid,
            },
            data: {
              rootGizmoOperation: {
                type: 'form.clear-processing',
              },
            },
          };

          events.next(event);
        }
      } else if (event.data?.gizmoOperation?.operation === 'form.cancel') {
        if (!parentRootGizmoFid) {
          await platformCallbacks?.platformOperationCB({
            operation: 'navigation.back',
            params: {},
          });
        } else {
          const params = rootGizmo.getParams();
          const event: IGizmoEvent = {
            type: 'root-gizmo.operation',
            src: {
              type: 'root-gizmo',
              rootGizmoFid: rootGizmo.getConfig().fid,
            },
            dest: {
              type: 'gizmo',
              rootGizmoFid: parentRootGizmoFid,
              gizmoFid: params['parent-gizmo-fid'],
            },
            data: {
              rootGizmoOperation: {
                type: 'child.form.lyfecycle.cancel',
                dataFid: data?.fid,
                dataId: data?.id,
              },
            },
          };

          callbacks.sendGlobalEventCB({ event });
        }
      } else if (event.data?.gizmoOperation?.operation === 'auth.logout') {
        await foundation
          ?.getGlobal()
          ?.getAnalytics()
          ?.createUserEvent({
            category: 'session',
            type: 'loggedOut',
            upload: true,
          });

        await foundation?.getPlatformInterface()?.logout();
      }
    } else if (
      event.type === 'platform-operation' &&
      event.data?.platformOperation &&
      event.data?.platformOperation?.operation === 'section.next'
    ) {
      const { ancestors } = event.data.platformOperation;

      if (ancestors) {
        const rootChildGizmos = rootGizmo.getGizmos();

        // The ancestor 0 is the RootGizmo
        const sectionAncestor = ancestors[1];
        const sectionGizmo = rootChildGizmos.find(
          g => g.getConfig().fid === sectionAncestor.fid
        ) as Gizmo | undefined;

        if (sectionGizmo) {
          let validationErrors: IError[] = await runHandlerOnGizmosTree({
            gizmo: sectionGizmo,
            handler: 'validateHDLR',
          });
          validationErrors = validationErrors.filter(
            vE => vE.features?.display
          );

          // This means all Gizmos are valid
          if (validationErrors.length === 0) {
            await sectionStateMachine?.next();
            // The state machine is going to give us the next section's fid
            const nextSectionFid = sectionStateMachine?.getCurrentState()
              ?.sectionFid;

            for (const rootChildGizmo of rootChildGizmos) {
              const sGizmo = rootChildGizmo as Gizmo;

              if (sGizmo.getConfig().fid === nextSectionFid) {
                sGizmo.setFeatures({
                  singleFeatures: [{ path: 'section.active', value: true }],
                });
                // After setting the next section to active we'll stop looping
                break;
              } else if (sGizmo.getConfig().type === 'section') {
                sGizmo.setFeatures({
                  singleFeatures: [{ path: 'section.active', value: false }],
                });
              }
            }
          }
        }
      }
    } else if (
      event.type === 'platform-operation' &&
      event.data?.platformOperation &&
      event.data.platformOperation.operation === 'section.back'
    ) {
      const { ancestors } = event.data.platformOperation;

      if (ancestors) {
        const rootChildGizmos = rootGizmo.getGizmos();
        // The ancestor 0 is the RootGizmo
        const sectionAncestor = ancestors[1];
        const sectionGizmo = rootChildGizmos.find(
          g => g.getConfig().fid === sectionAncestor.fid
        ) as Gizmo | undefined;

        if (sectionGizmo) {
          await sectionStateMachine?.back();
          // The state machine is going to give us the next section's fid
          const backSectionFid = sectionStateMachine?.getCurrentState()
            ?.sectionFid;
          const reversedRootChildGizmos = rootChildGizmos.reduce(
            (ary: any[], ele) => {
              ary.unshift(ele);
              return ary;
            },
            []
          );

          // We're reversing because we're going from back to front
          for (const rootChildGizmo of reversedRootChildGizmos) {
            const sGizmo = rootChildGizmo as Gizmo;

            if (sGizmo.getConfig().fid === backSectionFid) {
              sGizmo.setFeatures({
                singleFeatures: [{ path: 'section.active', value: true }],
              });
              // After setting the next section to active we'll stop looping
              break;
            } else if (sGizmo.getConfig().type === 'section') {
              sGizmo.setFeatures({
                singleFeatures: [{ path: 'section.active', value: false }],
              });
            }
          }
        }
      }
    } else if (
      event.type === 'platform-operation' &&
      event.data?.platformOperation?.operation === 'keyboard.key'
    ) {
      const { ancestors } = event.data.platformOperation;

      if (ancestors && ancestors.length === 1) {
        const rootChildGizmos = rootGizmo.getGizmos();

        // This means we're at the root gizmo
        if (event.src.gizmoFid) {
          const currentGizmoIndex = rootChildGizmos
            .map(g => g.getConfig().fid)
            .indexOf(event.src.gizmoFid);
          const currentGizmo: Gizmo = rootChildGizmos[
            currentGizmoIndex
          ] as Gizmo;

          if (
            currentGizmo.getFeatures().lastInSection &&
            currentGizmoIndex + 1 <= rootChildGizmos.length - 1
          ) {
            const nextGizmo: Gizmo = rootChildGizmos[
              currentGizmoIndex + 1
            ] as Gizmo;
            const nextConfig: IGizmo = nextGizmo.getConfig();

            if (nextConfig.ops?.button?.operation === 'form.submit') {
              const nextBinder: ButtonBinder = nextGizmo.getBinder();

              await nextBinder.dispatchOperation({
                operation: 'form.submit',
              });
            }
          }
        }
      } else if (ancestors) {
        const rootChildGizmos = rootGizmo.getGizmos();
        // In here we're going to check if we're inside a section
        const sectionAncestor = ancestors[1];
        const sectionGizmo = rootChildGizmos.find(
          g =>
            g.getConfig().fid === sectionAncestor.fid &&
            g.getConfig().type === 'section'
        ) as Gizmo | undefined;
        const sectionChildGizmos = sectionGizmo?.getGizmos();

        if (sectionGizmo && sectionChildGizmos && event.src.gizmoFid) {
          const currentGizmoIndex = sectionChildGizmos
            .map(g => g.getConfig().fid)
            .indexOf(event.src.gizmoFid);
          const currentGizmo: Gizmo = sectionChildGizmos[
            currentGizmoIndex
          ] as Gizmo;

          if (
            currentGizmo.getFeatures().lastInSection &&
            currentGizmoIndex + 1 <= sectionChildGizmos.length - 1
          ) {
            const buttons = findGizmos({
              gizmo: sectionGizmo,
              type: 'button',
            });
            // We're looking for a button with 'section.next' or 'form.save'
            // operation
            const button: Gizmo | undefined = buttons.find(
              g =>
                g.getConfig().ops?.button?.operation === 'section.next' ||
                g.getConfig().ops?.button?.operation === 'form.submit'
            ) as Gizmo;

            if (button) {
              const operation = button.getConfig().ops?.button?.operation;

              if (operation) {
                const nextBinder: ButtonBinder = button.getBinder();

                await nextBinder.dispatchOperation({
                  operation,
                });
              }
            }
          }
        }
      }
    }

    resolve();
  });
}

export default handleEvent;
